import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { usePageViewTracking, useGlobalClickTracking } from "../hooks/useAnalytics";
import { setUserContext, clearUserContext, updateInstanceContext } from "../utils/analyticsLogger";
import { getInstanceId, getSavedTenantId } from "../utils/authService";

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

function InstanceContextTracker() {
  const location = useLocation();
  const lastContextRef = useRef<string>("");
  
  useEffect(() => {
    const instanceId = getInstanceId();
    const tenantId = getSavedTenantId();
    
    const contextKey = `${instanceId}-${tenantId}`;
    
    if (contextKey !== lastContextRef.current && instanceId) {
      updateInstanceContext(instanceId, tenantId);
      lastContextRef.current = contextKey;
    }
  }, [location.pathname, location.search]);
  
  return null;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  usePageViewTracking();
  useGlobalClickTracking();

  return (
    <>
      <InstanceContextTracker />
      {children}
    </>
  );
}

export function AuthenticatedAnalyticsTracker({ userId, email }: { userId?: string; email?: string }) {
  const lastUserRef = useRef<string>("");
  
  useEffect(() => {
    const userKey = `${userId || "anonymous"}-${email || ""}`;
    
    if (userKey !== lastUserRef.current) {
      const instanceId = getInstanceId();
      const tenantId = getSavedTenantId();
      
      if (userId) {
        setUserContext({
          userId,
          email,
          instanceId: instanceId || undefined,
          tenantId: tenantId || undefined,
        });
      } else {
        clearUserContext();
      }
      lastUserRef.current = userKey;
    }
  }, [userId, email]);
  
  return null;
}
